"use client";

import React, { useState } from "react";
import { eventsApi } from "@/lib/api/services/events.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Save, Loader2, Calendar } from "lucide-react";
import Link from "next/link";
import { toast } from "@/components/ui/toast";

export default function EventsCreatePage() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [date, setDate] = useState("");
  const [type, setType] = useState("Workshop");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      toast({ type: "error", title: "Validation Error", message: "Event title is required." });
      return;
    }
    setIsSaving(true);
    try {
      await eventsApi.create({ title, description, location, date, type, status: "upcoming" });
      toast({ type: "success", title: "Event created", message: "Your event has been created." });
      setTitle("");
      setDescription("");
      setLocation("");
      setDate("");
      setType("Workshop");
    } catch {
      toast({ type: "error", title: "Error", message: "Failed to create event. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/events">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Link>
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-foreground">Create Event</h1>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <label htmlFor="event-title" className="block text-sm font-medium text-foreground mb-1">Event Title</label>
            <input
              id="event-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              placeholder="Enter event title"
            />
          </div>
          <div>
            <label htmlFor="event-description" className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              id="event-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              placeholder="Describe the event"
            />
          </div>
          <div>
            <label htmlFor="event-location" className="block text-sm font-medium text-foreground mb-1">Location</label>
            <input
              id="event-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              placeholder="Enter event location"
            />
          </div>
          <div>
            <label htmlFor="event-date" className="block text-sm font-medium text-foreground mb-1">Date</label>
            <input
              id="event-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </div>
          <div>
            <label htmlFor="event-type" className="block text-sm font-medium text-foreground mb-1">Type</label>
            <select
              id="event-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              <option value="Workshop">Workshop</option>
              <option value="Conference">Conference</option>
              <option value="Webinar">Webinar</option>
              <option value="Networking">Networking</option>
            </select>
          </div>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Create Event
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}